/**
 * MergeProfilesCard — coach-side reconciliation of placeholder athletes
 * to joined guardian accounts.
 *
 * Surfaces a card on the Roster page whenever there's at least one
 * placeholder athlete with a plausible match candidate. The coach is
 * shown a side-by-side comparison (placeholder vs joined account) and
 * picks which side wins on each field — per the user's directive:
 * "always ask the coach on merge profiles."
 *
 * The merge endpoint is idempotent; once the coach hits Merge the card
 * vanishes for that athlete and the joined guardian is now linked to
 * the roster row (so RSVPs, push targeting, etc. all work).
 */
import { useEffect, useState } from "react";
import { ArrowRight, Link2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton, ChalkCard } from "./primitives";

export default function MergeProfilesCard({ teamId, onMerged }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);

  async function load() {
    if (!teamId) { setItems([]); return; }
    try {
      const d = await api.mergeCandidates(teamId);
      setItems(d.items || []);
    } catch (e) {
      setItems([]);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [teamId]);

  if (!items || items.length === 0) return null;

  return (
    <>
      <ChalkCard data-testid="merge-card">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ marginTop: 2, color: "var(--green-mid)" }}>
            <Sparkles size={20} aria-hidden="true" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--chalk-board)" }}>
              {items.length === 1
                ? "1 roster spot looks like it just got claimed."
                : `${items.length} roster spots look like they just got claimed.`}
            </div>
            <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2, lineHeight: 1.4 }}>
              Match each placeholder to the right account so RSVPs and
              messages line up.
            </div>
          </div>
          <button
            type="button"
            data-testid="merge-card-open"
            onClick={() => setOpen(true)}
            style={{
              padding: "8px 14px", borderRadius: 10,
              background: "var(--chalk-board)", color: "#F5F0E8",
              border: "none", fontWeight: 700, fontSize: 12,
              letterSpacing: "0.06em", textTransform: "uppercase",
              cursor: "pointer", flexShrink: 0,
            }}
          >
            Review
          </button>
        </div>
      </ChalkCard>
      <MergeSheet
        isOpen={open}
        teamId={teamId}
        items={items}
        onDismiss={() => setOpen(false)}
        onMerged={() => { load(); onMerged && onMerged(); }}
      />
    </>
  );
}

// ── Merge bottom sheet ─────────────────────────────────────────────

function MergeSheet({ isOpen, teamId, items, onDismiss, onMerged }) {
  return (
    <BottomSheet isOpen={isOpen} title="Match accounts to the roster" onDismiss={onDismiss}>
      <div data-testid="merge-sheet" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {items.map((it) => (
          <MergeCandidate
            key={it.athlete.id}
            teamId={teamId}
            item={it}
            onMerged={onMerged}
          />
        ))}
        {items.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--chalk-muted)", padding: "8px 4px" }}>
            Nothing left to match. You're all set.
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

function MergeCandidate({ teamId, item, onMerged }) {
  const { athlete, candidates } = item;
  const [active, setActive] = useState(candidates[0]?.user?.id || null);
  // Per-field choice: 'placeholder' = keep the roster row's value,
  // 'joined' = use the joined user's value. The backend is permissive
  // (omitting any field defaults to keep-placeholder) but the UI is
  // always explicit so the coach knows what they're committing to.
  const [fields, setFields] = useState({
    first_name: "placeholder",
    last_name: "placeholder",
    display_name: "joined",  // joined account's name is the user-facing label
  });
  const [busy, setBusy] = useState(false);

  const cand = candidates.find((c) => c.user.id === active) || candidates[0];
  if (!cand) return null;

  async function submit() {
    setBusy(true);
    try {
      await api.mergeProfiles(teamId, {
        athlete_id: athlete.id,
        user_id: cand.user.id,
        fields,
      });
      toast.success(`Linked ${athlete.first_name} to ${cand.user.display_name}.`);
      onMerged && onMerged();
    } catch (e) {
      toast.error(e.message || "Couldn't merge.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={`merge-candidate-${athlete.id}`}
      style={{
        border: "0.5px solid var(--chalk-border)", borderRadius: 12,
        padding: 12, background: "var(--chalk-cream)",
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--chalk-board)" }}>
        {athlete.first_name} {athlete.last_name}
        {athlete.jersey ? ` · #${athlete.jersey}` : ""}
      </div>
      {candidates.length > 1 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: "var(--chalk-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
            Pick the account
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {candidates.map((c) => (
              <label
                key={c.user.id}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 10px", borderRadius: 8,
                  background: active === c.user.id ? "rgba(46,125,82,0.08)" : "transparent",
                  cursor: "pointer", fontSize: 13,
                }}
              >
                <input
                  type="radio"
                  name={`merge-${athlete.id}`}
                  checked={active === c.user.id}
                  onChange={() => setActive(c.user.id)}
                />
                <span style={{ flex: 1 }}>
                  {c.user.display_name} · {c.user.email}
                </span>
                <span style={{ fontSize: 10, color: "var(--chalk-muted)", textTransform: "uppercase" }}>
                  {c.match_reason} · {Math.round(c.match_score * 100)}%
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, color: "var(--chalk-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
          Which name wins?
        </div>
        <FieldChoice
          label="First name"
          placeholderValue={athlete.first_name}
          joinedValue={cand.user.first_name}
          choice={fields.first_name}
          onChoose={(v) => setFields({ ...fields, first_name: v })}
        />
        <FieldChoice
          label="Last name"
          placeholderValue={athlete.last_name}
          joinedValue={cand.user.last_name}
          choice={fields.last_name}
          onChoose={(v) => setFields({ ...fields, last_name: v })}
        />
      </div>
      <ChalkActionButton
        testId={`merge-confirm-${athlete.id}`}
        variant="primary"
        label={busy ? "Linking…" : `Link ${cand.user.first_name} → ${athlete.first_name}`}
        icon={<Link2 size={14} aria-hidden="true" />}
        loading={busy}
        onClick={submit}
      />
    </div>
  );
}

function FieldChoice({ label, placeholderValue, joinedValue, choice, onChoose }) {
  // Quietly hide rows where both sides agree — no decision needed.
  if (!placeholderValue && !joinedValue) return null;
  const same = (placeholderValue || "").trim().toLowerCase() ===
               (joinedValue || "").trim().toLowerCase();
  if (same) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
      <div style={{ width: 80, fontSize: 12, color: "var(--chalk-muted)" }}>{label}</div>
      <Pill
        active={choice === "placeholder"}
        label={placeholderValue || "—"}
        sub="roster"
        onClick={() => onChoose("placeholder")}
      />
      <ArrowRight size={12} aria-hidden="true" style={{ color: "var(--chalk-muted)" }} />
      <Pill
        active={choice === "joined"}
        label={joinedValue || "—"}
        sub="account"
        onClick={() => onChoose("joined")}
      />
    </div>
  );
}

function Pill({ active, label, sub, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1, padding: "6px 10px", borderRadius: 8,
        background: active ? "var(--chalk-board)" : "#fff",
        color: active ? "#F5F0E8" : "var(--chalk-board)",
        border: "0.5px solid var(--chalk-border)",
        cursor: "pointer", fontFamily: "inherit", fontSize: 13,
        textAlign: "left",
      }}
    >
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {sub}
      </div>
    </button>
  );
}
